// eslint-disable-next-line no-unused-vars
import React from 'react';
import {
  Grid,
  Box,
  Container,
  Grow,
  Skeleton,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import Business from '@mui/icons-material/Business';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useTranslation } from 'react-i18next';
import { useAuth, useDashboardData } from '../../hooks';
import {
  StatsCard,
  DashboardHero,
} from '../../components';
import { dashboardStyles as sx } from './HomePage.styles';
import CiroKontrolPaneli, { ALLOWED_ROLES as KONTROL_PANELI_ROLES } from './components/CiroKontrolPaneli';
import ConsultancyOverview from './components/ConsultancyOverview';

// --- Quick stats per-slot visual config ----------------------------------
// Order: [firms?, contracts, offers, tasks]. Firms slot only exists for
// ROOT/DIRECTOR (showFirmsCount inside useDashboardData), which means the
// quickStats array length is either 3 or 4. We key visuals by the i18n title
// so reorderings/role-filtering don't misalign icon + badge.
const STATS_VISUAL_BY_TITLE = {
  // Aktif Firma Sayısı — accent + green "Aktif" badge
  firms: {
    icon: Business,
    color: 'primary',
    badge: { label: 'Aktif', tone: 'success' },
  },
  // Son 3 Ay Sözleşme — green + neutral "3 Ay" badge
  contracts: {
    icon: FactCheckIcon,
    color: 'success',
    badge: { label: '3 Ay', tone: 'neutral' },
  },
  // Son 3 Ay Teklif — warning/orange + neutral "3 Ay" badge
  offers: {
    icon: DescriptionIcon,
    color: 'warning',
    badge: { label: '3 Ay', tone: 'neutral' },
  },
  // Bekleyen Görevler — purple + red "Bekliyor" badge + clock icon
  tasks: {
    icon: ChecklistIcon,
    color: 'purple',
    badge: { label: 'Bekliyor', tone: 'error', withClockIcon: true },
  },
};

// Maps a quickStats title back to one of the visual config keys. Uses t() so
// we still match after a language switch — the dashboardData hook also uses
// t() for the titles, so the same key resolves both sides.
const buildVisualLookup = (t) => ({
  [t('dashboard.quickStats.firms')]:     STATS_VISUAL_BY_TITLE.firms,
  [t('dashboard.quickStats.contracts')]: STATS_VISUAL_BY_TITLE.contracts,
  [t('dashboard.quickStats.offers')]:    STATS_VISUAL_BY_TITLE.offers,
  [t('dashboard.quickStats.tasks')]:     STATS_VISUAL_BY_TITLE.tasks,
});

const SectionHeader = ({ title, icon: Icon }) => (
  <Box sx={sx.sectionHeader}>
    <Box sx={sx.sectionLeft}>
      {Icon && (
        <Box sx={sx.sectionIconBox}>
          <Icon sx={{ fontSize: '1rem' }} />
        </Box>
      )}
      <Typography sx={sx.sectionTitle}>{title}</Typography>
    </Box>
    <Box sx={sx.sectionDivider} />
  </Box>
);

const StatsSkeleton = () => (
  <Grid container spacing={2.5}>
    {[1, 2, 3, 4].map(i => (
      <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
        <Skeleton
          variant="rectangular"
          height={140}
          sx={{
            borderRadius: '16px',
            transform: 'none',
            bgcolor: 'rgba(58,90,240,0.06)',
          }}
        />
      </Grid>
    ))}
  </Grid>
);

const ConsultancySkeleton = () => (
  <Skeleton
    variant="rectangular"
    height={360}
    sx={{
      borderRadius: '18px',
      transform: 'none',
      bgcolor: 'rgba(58,90,240,0.06)',
    }}
  />
);

const HomePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    loading,
    quickStats,
    projectStatusGroups,
    showTeamFilter,
    showPersonFilter,
    teams,
    teamUsers,
    selectedTeamId,
    selectedUserId,
    onTeamChange,
    onUserChange,
  } = useDashboardData();

  // Kontrol Paneli yalnizca belirli rollere gosterilir.
  const canSeeKontrolPaneli = KONTROL_PANELI_ROLES.includes(user?.role);

  const statsVisualByTitle = buildVisualLookup(t);

  return (
    <Box sx={sx.container}>
      <Container maxWidth="xl">

        <DashboardHero
          showTeamFilter={showTeamFilter}
          showPersonFilter={showPersonFilter}
          teams={teams}
          teamUsers={teamUsers}
          selectedTeamId={selectedTeamId}
          selectedUserId={selectedUserId}
          onTeamChange={onTeamChange}
          onUserChange={onUserChange}
        />

        {/* Hızlı İstatistikler */}
        <Grow in timeout={600}>
          <Box sx={{ mb: 4 }}>
            <SectionHeader
              title={t('dashboard.quickStats.title')}
              icon={BoltIcon}
            />
            {loading ? <StatsSkeleton /> : (
              <Grid container spacing={2.5}>
                {quickStats.map((stat, i) => {
                  const visual = statsVisualByTitle[stat.title] || {};
                  return (
                    <Grow in timeout={700 + i * 80} key={stat.title}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatsCard
                          title={stat.title}
                          value={stat.value}
                          icon={visual.icon || stat.icon}
                          color={visual.color || 'primary'}
                          badge={visual.badge}
                        />
                      </Grid>
                    </Grow>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Grow>

        {/* Danışmanlıklar — hero filtresinden etkilenir, bu yuzden hero'ya en yakin yerde */}
        <Grow in timeout={800}>
          <Box sx={{ mb: 4 }}>
            {loading ? <ConsultancySkeleton /> : (
              <ConsultancyOverview groups={projectStatusGroups} />
            )}
          </Box>
        </Grow>

        {/* Prim Performans Panosu — rol bazli gating, kendi filtresine sahip */}
        {canSeeKontrolPaneli && (
          <Grow in timeout={900}>
            <Box>
              <CiroKontrolPaneli />
            </Box>
          </Grow>
        )}

      </Container>
    </Box>
  );
};

export default HomePage;
